import { Check, Copy, Download, ExternalLink, FileCheck, FileText, ShieldCheck } from "lucide-react";
import { useState } from "react";
import Button from "@/components/ui/Button";
import Modal from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import type { TopUpRequest } from "@/types/wallet";
import { formatDate, formatINR, formatTime, TOPUP_STATUS_META } from "@/pages/retailer/wallet/data";

interface ReceiptPreviewModalProps {
  open: boolean;
  onClose: () => void;
  request: TopUpRequest | null;
}

export default function ReceiptPreviewModal({ open, onClose, request }: ReceiptPreviewModalProps) {
  const toast = useToast();
  const [copied, setCopied] = useState(false);

  if (!request) return null;

  const status = TOPUP_STATUS_META[request.status];
  const receiptUrl = (request as any).receiptUrl || (request as any).proofUrl || "";
  const isImage =
    receiptUrl.startsWith("data:image") ||
    /\.(png|jpe?g|webp|gif)$/i.test(request.receiptFileName || "");

  function copyReference() {
    if (request?.referenceNumber) {
      navigator.clipboard.writeText(request.referenceNumber);
      setCopied(true);
      toast.success("Reference number copied");
      setTimeout(() => setCopied(false), 2000);
    }
  }

  function downloadReceiptSlip() {
    const slipText = [
      "==================================================",
      "             E-MANDATE WALLET TOP-UP SLIP         ",
      "==================================================",
      `Request ID:       ${request.requestId}`,
      `Date & Time:      ${formatDate(request.requestedAt)} at ${formatTime(request.requestedAt)}`,
      `Status:           ${request.status}`,
      "--------------------------------------------------",
      `Retailer ID:      ${request.retailerId}`,
      `Shop / Business:  ${request.shopName || "N/A"}`,
      `Retailer Name:    ${request.retailerName || "N/A"}`,
      "--------------------------------------------------",
      `Amount:           INR ${request.amount.toLocaleString("en-IN")}`,
      `Payment Mode:     ${request.paymentMode}`,
      `Reference / UTR:  ${request.referenceNumber}`,
      `Receipt File:     ${request.receiptFileName || "None"}`,
      "--------------------------------------------------",
      `Admin Remark:     ${request.adminRemark || "None"}`,
      `Actioned At:      ${request.actionedAt ? `${formatDate(request.actionedAt)} ${formatTime(request.actionedAt)}` : "Pending"}`,
      "==================================================",
      "Generated by E-Mandate Platform Administration",
      "==================================================",
    ].join("\n");

    const blob = new Blob([slipText], { type: "text/plain;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", `receipt-${request.requestId}.txt`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Receipt downloaded successfully");
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Payment Receipt"
      size="md"
      footer={
        <div className="flex items-center justify-between border-t border-line px-5 py-4">
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Download className="h-4 w-4" />}
            onClick={downloadReceiptSlip}
          >
            Download Slip
          </Button>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {/* Top Summary Banner */}
        <div className="flex items-center justify-between rounded-xl bg-slate-900 px-4 py-3.5 text-white shadow-sm">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-wider text-slate-400">Top-Up Request</p>
            <p className="font-mono text-sm font-semibold tracking-wide text-white">{request.requestId}</p>
          </div>
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${status.bg} ${status.text}`}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-current" />
            {status.label}
          </span>
        </div>

        {/* Amount & Bank Voucher Card */}
        <div className="relative overflow-hidden rounded-xl border border-line bg-gradient-to-b from-white to-slate-50/60 p-5 shadow-xs">
          <div className="flex flex-col items-center justify-center border-b border-dashed border-line pb-4 text-center">
            <span className="text-xs font-medium uppercase tracking-wider text-ink-muted">Transaction Amount</span>
            <p className="mt-1 text-3xl font-extrabold text-primary-600">{formatINR(request.amount)}</p>
            <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-ink-secondary">
              <span>Mode: <strong className="text-ink">{request.paymentMode}</strong></span>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
            <div className="rounded-lg border border-line/70 bg-white p-2.5">
              <span className="block text-[11px] text-ink-secondary">Retailer</span>
              <span className="mt-0.5 block truncate font-semibold text-ink">
                {request.shopName || request.retailerId}
              </span>
              <span className="block font-mono text-[10px] text-ink-muted">{request.retailerId}</span>
            </div>

            <div className="rounded-lg border border-line/70 bg-white p-2.5">
              <div className="flex items-center justify-between">
                <span className="block text-[11px] text-ink-secondary">Reference / UTR</span>
                <button
                  type="button"
                  onClick={copyReference}
                  className="text-ink-muted hover:text-primary-600 cursor-pointer"
                  title="Copy Reference"
                >
                  {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
                </button>
              </div>
              <span className="mt-0.5 block truncate font-mono font-semibold text-ink">
                {request.referenceNumber || "—"}
              </span>
              <span className="block text-[10px] text-ink-muted">Bank confirmation ID</span>
            </div>

            <div className="rounded-lg border border-line/70 bg-white p-2.5">
              <span className="block text-[11px] text-ink-secondary">Payment Date</span>
              <span className="mt-0.5 block font-medium text-ink">{formatDate(request.requestedAt)}</span>
              <span className="block text-[10px] text-ink-muted">{formatTime(request.requestedAt)}</span>
            </div>

            <div className="rounded-lg border border-line/70 bg-white p-2.5">
              <span className="block text-[11px] text-ink-secondary">Verification</span>
              <span className="mt-0.5 inline-flex items-center gap-1 font-medium text-ink">
                <ShieldCheck className="h-3.5 w-3.5 text-success" />
                {request.status === "APPROVED" ? "Verified" : request.status === "REJECTED" ? "Rejected" : "Pending Audit"}
              </span>
              <span className="block truncate text-[10px] text-ink-muted">
                {request.adminRemark || "Awaiting admin confirmation"}
              </span>
            </div>
          </div>
        </div>

        {/* Document / Slip Preview Area */}
        <div className="rounded-xl border border-line bg-white p-4">
          <div className="mb-2.5 flex items-center justify-between">
            <label className="text-xs font-semibold uppercase tracking-wider text-ink-secondary">
              Attached Proof Document
            </label>
            {receiptUrl && (
              <a
                href={receiptUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-primary-500 hover:underline"
              >
                Open Original <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>

          {receiptUrl && isImage ? (
            <div className="overflow-hidden rounded-lg border border-line bg-slate-900 p-2 text-center">
              <img
                src={receiptUrl}
                alt={request.receiptFileName}
                className="mx-auto max-h-[260px] rounded object-contain"
              />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50/70 p-6 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-50 text-primary-600">
                <FileCheck className="h-6 w-6" />
              </span>
              <p className="font-mono text-sm font-semibold text-ink">
                {request.receiptFileName || "payment_receipt.png"}
              </p>
              <p className="max-w-xs text-xs text-ink-secondary">
                Payment slip uploaded by retailer via {request.paymentMode} for request #{request.requestId}.
              </p>
              <div className="mt-1 flex items-center gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  leftIcon={<Download className="h-3.5 w-3.5" />}
                  onClick={downloadReceiptSlip}
                >
                  Download Receipt Record
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
